import { Button, Radio, RadioGroup, RadioGroupDirection, TextArea } from '@base-app';
import { InfoCard } from '@builder';
import { ChangeEvent, FC, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import {
  ActionType,
  DeclainedReasonId,
  GCConsentOptionId,
  PatientContactOptionId,
  ResultsSharingOptionId,
  SchedulerInquiryProps,
} from './SchedulerInquiry.types';
import { initialState, schedulerInquiryReducer } from './schedulerInquiryReducer';
import {
  StyledContactButtonsContainer,
  StyledReasonsContainer,
  infoCardStyles,
  separation,
} from './SchedulerInquiry.styles';
import { formatISO } from 'date-fns';
import { Variants } from '@/common/types';
import CallToAction from '../shared/CallToAction/CallToAction';
import { COMMON_TRANSLATION } from '@/common/constants';
import { EventTypeId } from '../../types';

const SchedulerInquiry: FC<SchedulerInquiryProps> = ({
  disabled,
  handleContactAttempt,
  handleDeclineReferral,
  handleInquiryChange,
  isContactAttemptSuccess,
}) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [inquiry, dispatch] = useReducer(schedulerInquiryReducer, initialState);
  const [declinedReasonNotes, setDeclinedReasonNotes] = useState<string | undefined>(undefined);

  useEffect(() => {
    inquiry.patientContacted !== undefined &&
    inquiry.patientContacted &&
    inquiry.geneticCounselingConsent !== undefined &&
    inquiry.geneticCounselingConsent &&
    inquiry.resultsSharingConsent !== undefined &&
    inquiry.resultsSharingConsent
      ? handleInquiryChange(true, inquiry)
      : handleInquiryChange(false, inquiry);
  }, [inquiry]);

  useEffect(() => {
    // Resets inquiry state if disable is true
    if (disabled || isContactAttemptSuccess) dispatch({ type: ActionType.RESET });
  }, [disabled, isContactAttemptSuccess]);

  const handlePatientContacted = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    dispatch({
      type: ActionType.CONTACT,
      payload: {
        value: value === PatientContactOptionId.CONTACTED,
        timestamp: formatISO(new Date()),
      },
    });
    value === PatientContactOptionId.CONTACTED &&
      declinedReasonNotes !== undefined &&
      setDeclinedReasonNotes(undefined);
  };

  const handleGeneticCounselingConsent = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    dispatch({
      type: ActionType.GENETIC_COUNSELING_CONSENT,
      payload: {
        value: value === GCConsentOptionId.GC_ACCEPTED,
        timestamp: formatISO(new Date()),
      },
    });
    value === GCConsentOptionId.GC_ACCEPTED && declinedReasonNotes !== undefined && setDeclinedReasonNotes(undefined);
  };

  const handleDeclineReason = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    dispatch({
      type: ActionType.DECLINE,
      payload: {
        declineReason: value,
      },
    });
  };

  const handleResultsSharingConsent = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    dispatch({
      type: ActionType.RESULTS_SHARING_CONSENT,
      payload: {
        value: value === ResultsSharingOptionId.RESULTS_SHARING_ACCEPTED,
        timestamp: formatISO(new Date()),
      },
    });
  };

  return (
    <InfoCard css={infoCardStyles} hasBorder>
      <RadioGroup
        data-testid="patient-contact-radio-group"
        direction={RadioGroupDirection.COLUMN}
        disabled={disabled}
        id="patient-contact"
        name="patient-contact"
        label={t('scheduler.patient_contact_status')}
        onChange={handlePatientContacted}
        reset={isContactAttemptSuccess}
      >
        <Radio
          data-testid="patient-contacted-radio-option"
          id={PatientContactOptionId.CONTACTED}
          name={PatientContactOptionId.CONTACTED}
          value={PatientContactOptionId.CONTACTED}
        >
          {t('scheduler.patient_contacted')}
        </Radio>
        <Radio
          data-testid="unable-to-contact-radio-option"
          id={PatientContactOptionId.UNABLE_TO_CONTACT}
          name={PatientContactOptionId.UNABLE_TO_CONTACT}
          value={PatientContactOptionId.UNABLE_TO_CONTACT}
        >
          {t('scheduler.unable_to_contact')}
        </Radio>
      </RadioGroup>

      {inquiry.patientContacted !== undefined && !inquiry.patientContacted && (
        <>
          <TextArea
            data-testid="patient-contact-notes-textarea"
            id="patient-contact-notes-textarea"
            name="patient-contact-notes-textarea"
            label={t('notes', { ns: COMMON_TRANSLATION })}
            onChange={(e) => setDeclinedReasonNotes((e.target as HTMLTextAreaElement).value)}
          />
          <StyledContactButtonsContainer>
            <Button
              css={{ height: 'fit-content', marginRight: '$6', maxWidth: '130px' }}
              data-testid="contact-at-another-time-button"
              variant={Variants.SECONDARY}
              onClick={() =>
                handleContactAttempt({
                  declinedReasonNotes,
                  event: EventTypeId.attempt,
                  eventDateTime: formatISO(new Date()),
                })
              }
            >
              {t('scheduler.contact_ahother_time')}
            </Button>
            <Button
              css={{ maxWidth: '220px', height: 'fit-content' }}
              data-testid="multiple-attempts-made-button"
              variant={Variants.SECONDARY}
              onClick={() =>
                handleDeclineReferral({
                  declinedReasonNotes,
                  event: EventTypeId.decline,
                  eventDateTime: formatISO(new Date()),
                  reasonCode: DeclainedReasonId.MULTIPLE_ATTEMPTS_MADE,
                  reasonDescription: t(`declined.reason.${DeclainedReasonId.MULTIPLE_ATTEMPTS_MADE}`),
                })
              }
            >
              {t('scheduler.unable_to_contact_multiple_attempts')}
            </Button>
          </StyledContactButtonsContainer>
        </>
      )}

      <RadioGroup
        css={separation}
        data-testid="genetic-counseling-consent-radio-group"
        direction={RadioGroupDirection.COLUMN}
        disabled={disabled || !inquiry.patientContacted}
        id="genetic-counseling-consent"
        name="genetic-counseling-consent"
        label={t('scheduler.patient_consent_provided')}
        onChange={handleGeneticCounselingConsent}
      >
        <Radio
          data-testid="genetic-counseling-accepted-radio-option"
          id={GCConsentOptionId.GC_ACCEPTED}
          name={GCConsentOptionId.GC_ACCEPTED}
          value={GCConsentOptionId.GC_ACCEPTED}
        >
          {t('scheduler.yes_gc_consent')}
        </Radio>
        <Radio
          data-testid="genetic-counseling-declined-radio-option"
          id={GCConsentOptionId.GC_DECLINED}
          name={GCConsentOptionId.GC_DECLINED}
          value={GCConsentOptionId.GC_DECLINED}
        >
          {t('scheduler.no_gc_consent')}
        </Radio>
      </RadioGroup>

      {inquiry.patientContacted === undefined ||
        !inquiry.patientContacted ||
        (inquiry.geneticCounselingConsent !== undefined && !inquiry.geneticCounselingConsent && (
          <StyledReasonsContainer>
            <RadioGroup
              css={separation}
              data-testid="genetic-counseling-declined-reason-radio-group"
              direction={RadioGroupDirection.COLUMN}
              id="genetic-counseling-declined-reason"
              label={t('scheduler.select_decline_reason')}
              labelStyles={{ fontSize: '$md', fontWeight: '$medium', color: '$black' }}
              name="genetic-counseling-declined-reason"
              onChange={handleDeclineReason}
            >
              <Radio
                data-testid="already-aware-of-result-radio-option"
                id={DeclainedReasonId.ALREADY_AWARE_OF_RESULT}
                name={DeclainedReasonId.ALREADY_AWARE_OF_RESULT}
                value={DeclainedReasonId.ALREADY_AWARE_OF_RESULT}
              >
                {t(`declined.reason.${DeclainedReasonId.ALREADY_AWARE_OF_RESULT}`)}
              </Radio>
              <Radio
                data-testid="already-received-gc-radio-option"
                id={DeclainedReasonId.ALREADY_RECEIVED_GC}
                name={DeclainedReasonId.ALREADY_RECEIVED_GC}
                value={DeclainedReasonId.ALREADY_RECEIVED_GC}
              >
                {t(`declined.reason.${DeclainedReasonId.ALREADY_RECEIVED_GC}`)}
              </Radio>
              <Radio
                data-testid="not-interested-radio-option"
                id={DeclainedReasonId.NOT_INTERESTED}
                name={DeclainedReasonId.NOT_INTERESTED}
                value={DeclainedReasonId.NOT_INTERESTED}
              >
                {t(`declined.reason.${DeclainedReasonId.NOT_INTERESTED}`)}
              </Radio>
              <Radio
                data-testid="other-radio-option"
                id={DeclainedReasonId.OTHER}
                name={DeclainedReasonId.OTHER}
                value={DeclainedReasonId.OTHER}
              >
                {t(`declined.reason.${DeclainedReasonId.OTHER}`)}
              </Radio>
            </RadioGroup>
            {inquiry.declineReason === DeclainedReasonId.OTHER && (
              <TextArea
                data-testid="decline-reason-other-textarea"
                id="decline-reason-other-textarea"
                name="decline-reason-other-textarea"
                onChange={(e) => setDeclinedReasonNotes((e.target as HTMLTextAreaElement).value)}
              />
            )}
            <CallToAction
              acctionButtonText={t('proceed')}
              disabled={inquiry.declineReason === undefined}
              handleActionClick={() =>
                handleDeclineReferral({
                  declinedReasonNotes,
                  event: EventTypeId.decline,
                  eventDateTime: formatISO(new Date()),
                  reasonCode: inquiry.declineReason,
                  reasonDescription:
                    inquiry.declineReason !== undefined ? t(`declined.reason.${inquiry.declineReason}`) : undefined,
                })
              }
              message={t('patient_declined_message')}
              testId="patient-declined-gc-call-to-action"
            />
          </StyledReasonsContainer>
        ))}

      <RadioGroup
        css={separation}
        data-testid="results-sharing-consent-radio-group"
        direction={RadioGroupDirection.COLUMN}
        disabled={disabled || !inquiry.geneticCounselingConsent || !inquiry.patientContacted}
        id="results-sharing-consent"
        name="results-sharing-consent"
        label={t('scheduler.patient_share_consent_provided')}
        onChange={handleResultsSharingConsent}
      >
        <Radio
          data-testid="results-sharing-accepted-radio-option"
          id={ResultsSharingOptionId.RESULTS_SHARING_ACCEPTED}
          name={ResultsSharingOptionId.RESULTS_SHARING_ACCEPTED}
          value={ResultsSharingOptionId.RESULTS_SHARING_ACCEPTED}
        >
          {t('scheduler.yes_share_consent')}
        </Radio>
        <Radio
          data-testid="results-sharing-declined-radio-option"
          id={ResultsSharingOptionId.RESULTS_SHARING_DECLINED}
          name={ResultsSharingOptionId.RESULTS_SHARING_DECLINED}
          value={ResultsSharingOptionId.RESULTS_SHARING_DECLINED}
        >
          {t('scheduler.no_share_consent')}
        </Radio>
      </RadioGroup>
      {inquiry.patientContacted === undefined ||
        !inquiry.patientContacted ||
        inquiry.geneticCounselingConsent === undefined ||
        !inquiry.geneticCounselingConsent ||
        (inquiry.resultsSharingConsent !== undefined && !inquiry.resultsSharingConsent && (
          <CallToAction
            acctionButtonText={t('proceed')}
            handleActionClick={() =>
              handleDeclineReferral({
                declinedReasonNotes,
                event: EventTypeId.decline,
                eventDateTime: formatISO(new Date()),
                reasonCode: DeclainedReasonId.DOES_NOT_WANT_TO_SHARE,
                reasonDescription: t(`declined.reason.${DeclainedReasonId.DOES_NOT_WANT_TO_SHARE}`),
              })
            }
            message={t('scheduler.patient_not_elegible')}
            testId="patient-not-elegible-call-to-action"
          />
        ))}
    </InfoCard>
  );
};

export default SchedulerInquiry;
